import type { LearningExercise, SourceRef } from "../types";
import { makeCloze, makeFlashcard, makeMulti, makeSingle } from "./legalExerciseFactory";

// EPC-Grundlagen: EPÜ Art. 52/54/56/87/123, PCT, PatG §§ 9/10/14, BGB
// Migrated from questions/epc-grundlagen.js — mechanical format migration only,
// no content changes. IDs preserved for SRS continuity.
// reviewStatus preserved from source (mostly "beta-ready").

const pathId = "epu-eqe" as const;

const sources: Record<string, SourceRef> = {
  art52: { id: "art52", label: "Art. 52 EPÜ", url: "https://www.epo.org/de/legal/epc/2020/a52.html" },
  art54: { id: "art54", label: "Art. 54 EPÜ", url: "https://www.epo.org/de/legal/epc/2020/a54.html" },
  art56: { id: "art56", label: "Art. 56 EPÜ", url: "https://www.epo.org/de/legal/epc/2020/a56.html" },
  art87: { id: "art87", label: "Art. 87 EPÜ", url: "https://www.epo.org/de/legal/epc/2020/a87.html" },
  art123: { id: "art123", label: "Art. 123 EPÜ", url: "https://www.epo.org/de/legal/epc/2020/a123.html" },
  psa: { id: "psa", label: "EPA-Richtlinien G-VII, 5", url: "https://www.epo.org/de/legal/guidelines-epc/2026/g_vii_5.html" },
  psaClosest: { id: "psaClosest", label: "EPA-Richtlinien G-VII, 5.1", url: "https://www.epo.org/de/legal/guidelines-epc/2026/g_vii_5_1.html" },
  psaProblem: { id: "psaProblem", label: "EPA-Richtlinien G-VII, 5.2", url: "https://www.epo.org/de/legal/guidelines-epc/2026/g_vii_5_2.html" },
  addedMatter: { id: "addedMatter", label: "EPA-Richtlinien H-IV, 2.1", url: "https://www.epo.org/de/legal/guidelines-epc/2026/h_iv_2_1.html" },
  pct3: { id: "pct3", label: "Art. 3 PCT", url: "https://www.wipo.int/de/web/pct-system/texts/articles/a3" },
  pct8: { id: "pct8", label: "Art. 8 PCT", url: "https://www.wipo.int/de/web/pct-system/texts/articles/a8" },
  pct11: { id: "pct11", label: "Art. 11 PCT", url: "https://www.wipo.int/de/web/pct-system/texts/articles/a11" },
  pct15: { id: "pct15", label: "Art. 15 PCT", url: "https://www.wipo.int/de/web/pct-system/texts/articles/a15" },
  pct33: { id: "pct33", label: "Art. 33 PCT", url: "https://www.wipo.int/de/web/pct-system/texts/articles/a33" },
  patg1: { id: "patg1", label: "§ 1 PatG", url: "https://www.gesetze-im-internet.de/patg/__1.html" },
  patg3: { id: "patg3", label: "§ 3 PatG", url: "https://www.gesetze-im-internet.de/patg/__3.html" },
  patg9: { id: "patg9", label: "§ 9 PatG", url: "https://www.gesetze-im-internet.de/patg/__9.html" },
  patg10: { id: "patg10", label: "§ 10 PatG", url: "https://www.gesetze-im-internet.de/patg/__10.html" },
  patg14: { id: "patg14", label: "§ 14 PatG", url: "https://www.gesetze-im-internet.de/patg/__14.html" },
  bgb145: { id: "bgb145", label: "§ 145 BGB", url: "https://www.gesetze-im-internet.de/bgb/__145.html" },
  bgb158: { id: "bgb158", label: "§ 158 BGB", url: "https://www.gesetze-im-internet.de/bgb/__158.html" },
  bgb164: { id: "bgb164", label: "§ 164 BGB", url: "https://www.gesetze-im-internet.de/bgb/__164.html" },
  bgb194: { id: "bgb194", label: "§ 194 BGB", url: "https://www.gesetze-im-internet.de/bgb/__194.html" },
};

export const epcGrundlagen: LearningExercise[] = [

  // ─── SINGLE (33) ────────────────────────────────────────────────────────────

  makeSingle(sources, {
    id: "novelty-state-of-art",
    pathId,
    topic: "Neuheit",
    title: "Was gehört zum Stand der Technik?",
    prompt: "Welche Aussage trifft den Grundgedanken des Standes der Technik nach Art. 54(2) EPÜ am besten?",
    options: [
      "Nur technische Informationen, die schriftlich dokumentiert und auffindbar sind.",
      "Alles, was der Öffentlichkeit vor dem Anmeldetag schriftlich, mündlich, durch Benutzung oder sonst wie zugänglich gemacht wurde.",
      "Nur Offenbarungen, die ein Fachmann ohne zusätzliche Recherche tatsächlich kannte.",
      "Nur Veröffentlichungen, die denselben Zweck wie die beanspruchte Erfindung betreffen.",
    ],
    answer: 1,
    explanation:
      "Art. 54(2) EPÜ ist breit: Stand der Technik ist alles, was vor dem Anmeldetag der Öffentlichkeit zugänglich gemacht wurde - schriftlich, mündlich, durch Benutzung oder in sonstiger Weise. Entscheidend ist die öffentliche Zugänglichkeit, nicht die Form. Antwort A ist zu eng (nur schriftlich).",
    sourceIds: ["art54"],
    levelNumber: 2,
    errorCategory: "Regel nicht erinnert",
    skillTags: ["neuheit"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "art54-3-use",
    pathId,
    topic: "Neuheit",
    title: "Art. 54(3) EPÜ richtig einordnen",
    prompt:
      "Eine ältere europäische Anmeldung wurde vor deinem Anmeldetag eingereicht, aber erst danach veröffentlicht. Wofür ist sie nach dem EPÜ typischerweise relevant?",
    options: [
      "Für Neuheit; für erfinderische Tätigkeit wird sie nach Art. 56 EPÜ nicht herangezogen.",
      "Für Neuheit und erfinderische Tätigkeit, sofern der Fachmann sie später ohne Aufwand finden konnte.",
      "Für erfinderische Tätigkeit, wenn sie technisch besonders nahe liegt, aber nicht für Neuheit.",
      "Für Neuheit nur dann, wenn sie vor dem Anmeldetag bereits faktisch bekannt war.",
    ],
    answer: 0,
    explanation:
      "Art. 54(3) EPÜ erweitert den Stand der Technik für Neuheit. Art. 56 EPÜ nimmt solche Dokumente für die Beurteilung der erfinderischen Tätigkeit aus.",
    sourceIds: ["art54"],
    levelNumber: 3,
    errorCategory: "Voraussetzung verwechselt",
    skillTags: ["neuheit"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "inventive-step-basic",
    pathId,
    topic: "Erfinderische Tätigkeit",
    title: "Erfinderische Tätigkeit in einem Satz",
    prompt: "Welche Formulierung trifft Art. 56 EPÜ am besten?",
    options: [
      "Eine Erfindung ist erfinderisch, wenn sie eine technische Wirkung gegenüber einem geeigneten Ausgangspunkt plausibel macht.",
      "Eine Erfindung beruht auf erfinderischer Tätigkeit, wenn sie sich für den Fachmann nicht in naheliegender Weise aus dem Stand der Technik ergibt.",
      "Eine Erfindung ist erfinderisch, wenn sie gegenüber einem einzelnen Dokument neu ist.",
      "Eine Erfindung ist erfinderisch, wenn ihre Umsetzung mehr Entwicklungsaufwand verursacht als bekannte Lösungen.",
    ],
    answer: 1,
    explanation:
      "Art. 56 EPÜ fragt nicht nach Komplexität oder Markterfolg als solchem, sondern nach Naheliegen für den Fachmann im Lichte des Standes der Technik.",
    sourceIds: ["art56"],
    levelNumber: 2,
    errorCategory: "Regel nicht erinnert",
    skillTags: ["erfinderische-taetigkeit"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "psa-steps",
    pathId,
    topic: "Problem-Lösungs-Ansatz",
    title: "Drei Schritte im Problem-Lösungs-Ansatz",
    prompt: "Welche Reihenfolge bildet den Problem-Lösungs-Ansatz der EPO am besten ab?",
    options: [
      "Nächstliegenden Stand der Technik bestimmen, Unterschiede identifizieren und daraus die objektive technische Aufgabe ableiten.",
      "Nächstliegenden Stand der Technik bestimmen, objektive technische Aufgabe formulieren, Naheliegen prüfen.",
      "Die wirtschaftliche Aufgabe formulieren, den Marktwert bestimmen und anschließend den Anspruch anpassen.",
      "Alle Dokumente mit ähnlichen Merkmalen kombinieren und danach prüfen, ob die Kombination neu ist.",
    ],
    answer: 1,
    explanation:
      "Die Guidelines nennen als Kern: closest prior art, objective technical problem und die Frage, ob die beanspruchte Lösung naheliegend gewesen wäre.",
    sourceIds: ["psa"],
    levelNumber: 3,
    errorCategory: "Prüfungsstruktur unsauber",
    skillTags: ["erfinderische-taetigkeit"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "psa-pointer",
    pathId,
    topic: "Problem-Lösungs-Ansatz",
    title: "Die Aufgabe darf die Lösung nicht verraten",
    prompt:
      "Warum sollte die objektive technische Aufgabe im Problem-Lösungs-Ansatz nicht schon einen Hinweis auf die beanspruchte Lösung enthalten?",
    options: [
      "Weil die Aufgabe sonst rückschauend formuliert wird und die Prüfung des Naheliegens verzerrt.",
      "Weil technische Aufgaben im EPÜ nicht erwähnt werden.",
      "Weil Aufgaben immer rein wirtschaftlich formuliert werden müssen.",
      "Weil nur der Anmelder eine Aufgabe formulieren darf.",
    ],
    answer: 0,
    explanation:
      "Eine lösungsnahe Aufgabenformulierung kann den Weg zur Erfindung künstlich vorzeichnen. Genau das soll eine objektive Betrachtung vermeiden.",
    sourceIds: ["psaProblem"],
    levelNumber: 4,
    errorCategory: "Begründung unvollständig",
    skillTags: ["erfinderische-taetigkeit"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "priority-12-months",
    pathId,
    topic: "Priorität",
    title: "Prioritätsfrist als Grundmuster",
    prompt: "Welchen Zeitraum nennt Art. 87(1) EPÜ für das Prioritätsrecht ab der ersten Anmeldung?",
    options: [
      "6 Monate",
      "9 Monate",
      "12 Monate",
      "18 Monate",
    ],
    answer: 2,
    explanation:
      "Art. 87(1) EPÜ nennt für Patente ein Prioritätsrecht während zwölf Monaten ab dem Anmeldetag der ersten Anmeldung.",
    sourceIds: ["art87"],
    levelNumber: 2,
    errorCategory: "Frist falsch erinnert",
    skillTags: ["fristen-prioritaet"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "priority-same-invention",
    pathId,
    topic: "Priorität",
    title: "Priorität ist keine pauschale Reservierung",
    prompt:
      "Eine spätere EP-Anmeldung enthält eine technische Variante, die in der ersten Anmeldung nicht offenbart war. Was ist der kritischste Prüfpunkt?",
    options: [
      "Ob dieselbe Erfinderperson beteiligt ist.",
      "Ob die spätere Variante als dieselbe Erfindung aus der ersten Anmeldung hervorgeht.",
      "Ob die Variante wirtschaftlich wichtiger ist.",
      "Ob die erste Anmeldung schon veröffentlicht wurde.",
    ],
    answer: 1,
    explanation:
      "Priorität trägt nur für den Gegenstand, der als dieselbe Erfindung aus der Prioritätsanmeldung ableitbar ist. Neue technische Varianten brauchen eine eigene Prüfung.",
    sourceIds: ["art87"],
    levelNumber: 3,
    errorCategory: "Sachverhaltsdetail überlesen",
    skillTags: ["fristen-prioritaet"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "added-matter-basic",
    pathId,
    topic: "Änderungen",
    title: "Art. 123(2) EPÜ",
    prompt: "Welche Grundregel steckt hinter Art. 123(2) EPÜ?",
    options: [
      "Eine Anmeldung darf nicht so geändert werden, dass sie Gegenstand enthält, der über den Inhalt der Anmeldung in der ursprünglich eingereichten Fassung hinausgeht.",
      "Ein Anspruch darf nach Einreichung nie geändert werden.",
      "Nur die Beschreibung darf geändert werden, nicht die Ansprüche.",
      "Änderungen sind immer erlaubt, wenn sie den Anspruch enger machen.",
    ],
    answer: 0,
    explanation:
      "Art. 123(2) EPÜ schützt vor unzulässiger Erweiterung. Auch einschränkende Änderungen können kritisch sein, wenn die konkrete Kombination nicht ursprünglich offenbart war.",
    sourceIds: ["art123"],
    levelNumber: 2,
    errorCategory: "Ausnahme übersehen",
    skillTags: ["added-matter"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "post-grant-extension",
    pathId,
    topic: "Änderungen",
    title: "Art. 123(3) EPÜ",
    prompt: "Was verbietet Art. 123(3) EPÜ bei einem erteilten europäischen Patent?",
    options: [
      "Jede Änderung nach der Erteilung.",
      "Eine Änderung, durch die der Schutzbereich erweitert wird.",
      "Jede Beschränkung des Anspruchs.",
      "Jede Änderung der Beschreibung.",
    ],
    answer: 1,
    explanation:
      "Nach Erteilung darf das Patent nicht so geändert werden, dass der Schutzbereich erweitert wird.",
    sourceIds: ["art123"],
    levelNumber: 2,
    errorCategory: "Regel nicht erinnert",
    skillTags: ["added-matter"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "claim-mapping-novelty",
    pathId,
    topic: "Claim Mapping",
    title: "Merkmale gegen ein Dokument lesen",
    prompt:
      "Anspruch: Sensor, Controller und verschlüsselte drahtlose Übertragung. Dokument D1 offenbart Sensor, Controller und drahtgebundene Übertragung. Welche Aussage ist für Neuheit gegenüber D1 am saubersten?",
    options: [
      "D1 nimmt den Anspruch vollständig neuheitsschädlich vorweg.",
      "Gegenüber D1 fehlt mindestens das Merkmal der verschlüsselten drahtlosen Übertragung.",
      "D1 ist irrelevant, weil nur Produkte am Markt zählen.",
      "D1 kann nur für erfinderische Tätigkeit verwendet werden.",
    ],
    answer: 1,
    explanation:
      "Für Neuheit muss ein einzelner Stand der Technik alle Anspruchsmerkmale unmittelbar und eindeutig offenbaren. Hier fehlt in D1 mindestens ein Merkmal.",
    sourceIds: ["art54"],
    levelNumber: 3,
    errorCategory: "Anspruchsmerkmal falsch zugeordnet",
    skillTags: ["neuheit", "claim-analysis"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "claim-mapping-combination",
    pathId,
    topic: "Claim Mapping",
    title: "Neuheit ist nicht Dokumenten-Mix",
    prompt:
      "D1 offenbart Sensor und Controller. D2 offenbart verschlüsselte drahtlose Übertragung in anderem Kontext. Welche Prüfung ist typischerweise eher betroffen, wenn man D1 und D2 kombinieren möchte?",
    options: [
      "Neuheit, weil man beliebige Dokumente addieren darf.",
      "Erfinderische Tätigkeit, weil Kombinationen mehrerer Quellen dort relevant werden können.",
      "Priorität, weil D2 immer Priorität zerstört.",
      "Art. 123(3), weil D2 nach Erteilung liegt.",
    ],
    answer: 1,
    explanation:
      "Neuheit wird grundsätzlich gegen eine einzelne Offenbarung geprüft. Kombinationen verschiedener Quellen sind eher eine Frage der erfinderischen Tätigkeit.",
    sourceIds: ["art56"],
    levelNumber: 4,
    errorCategory: "Prüfungsstruktur unsauber",
    skillTags: ["erfinderische-taetigkeit", "neuheit"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "public-disclosure",
    pathId,
    topic: "Offenbarung",
    title: "Pitch vor Anmeldung",
    prompt:
      "Ein Gründer erklärt eine technische Lösung vor Einreichung ohne Geheimhaltungspflicht in einem öffentlichen Pitch. Was ist der zentrale IP-Risikopunkt?",
    options: [
      "Die Erklärung kann als öffentliche Offenbarung Stand der Technik werden.",
      "Die Erklärung ist nur dann relevant, wenn sie später schriftlich dokumentiert wurde.",
      "Die Erklärung ist unschädlich, wenn der Pitch wirtschaftlich erfolglos blieb.",
      "Die Erklärung betrifft nur Geheimnisschutz, aber nie den Stand der Technik.",
    ],
    answer: 0,
    explanation:
      "Art. 54(2) EPÜ umfasst auch mündliche Beschreibung, Benutzung oder sonstige öffentliche Zugänglichmachung.",
    sourceIds: ["art54"],
    levelNumber: 3,
    errorCategory: "Ausnahme übersehen",
    skillTags: ["neuheit"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "patentable-inventions",
    pathId,
    topic: "Patentierbarkeit",
    title: "Art. 52 EPÜ als Türöffner",
    prompt: "Welche Anforderungen nennt Art. 52(1) EPÜ für europäische Patente in allen Gebieten der Technik?",
    options: [
      "Neuheit, erfinderische Tätigkeit und gewerbliche Anwendbarkeit.",
      "Markterfolg, Investoreninteresse und technische Schönheit.",
      "Nur Neuheit.",
      "Nur gewerbliche Anwendbarkeit.",
    ],
    answer: 0,
    explanation:
      "Art. 52(1) EPÜ nennt Neuheit, erfinderische Tätigkeit und gewerbliche Anwendbarkeit.",
    sourceIds: ["art52"],
    levelNumber: 2,
    errorCategory: "Regel nicht erinnert",
    skillTags: ["patentierbarkeit"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "exam-answer-quality",
    pathId,
    topic: "Prüfungstechnik",
    title: "Antwortqualität statt Stichwortsammlung",
    prompt: "Welche Antworttechnik ist in einer Fallfrage meist tragfähiger?",
    options: [
      "Nur die Norm nennen und sofort zum nächsten Punkt springen.",
      "Regel nennen, auf den Sachverhalt anwenden und die Schlussfolgerung knapp begründen.",
      "Möglichst viele Begriffe auflisten.",
      "Die wirtschaftliche Bedeutung zuerst diskutieren und die Norm weglassen.",
    ],
    answer: 1,
    explanation:
      "Für prüfungsnahe Anwendung zählt die strukturierte Begründung: Regel, Subsumtion, Ergebnis. Reine Stichworte sind oft zu dünn.",
    sourceIds: ["psa"],
    levelNumber: 3,
    errorCategory: "Begründung unvollständig",
    skillTags: ["pruefungstechnik"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "pct-international-application",
    pathId,
    topic: "PCT",
    title: "Was der PCT nicht ist",
    prompt: "Welche Aussage trifft die Grundlogik des PCT am besten?",
    options: [
      "Der PCT bündelt die materielle Patentprüfung aller Vertragsstaaten in einer einzigen internationalen Erteilung.",
      "Der PCT schafft ein internationales Anmeldeverfahren, ersetzt aber nicht die spätere nationale oder regionale Patentprüfung.",
      "Der PCT verschiebt die Länderentscheidungen so, dass nationale Phasen später nur noch Formalität sind.",
      "Der PCT ersetzt nationale Phasen, wenn der internationale Recherchenbericht positiv ist.",
    ],
    answer: 1,
    explanation:
      "Der PCT erleichtert den Weg über eine internationale Anmeldung. Er führt aber nicht selbst zu einem einheitlichen Weltpatent.",
    sourceIds: ["pct3"],
    levelNumber: 1,
    errorCategory: "Systemlogik verwechselt",
    skillTags: ["pct"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "pct-priority-12-months",
    pathId,
    topic: "PCT",
    title: "PCT und Priorität",
    prompt: "Warum ist die 12-Monats-Prioritätsfrist auch für PCT-Strategie zentral?",
    options: [
      "Weil eine internationale Anmeldung häufig innerhalb der Prioritätsfrist eingereicht wird, um den früheren Anmeldetag zu beanspruchen.",
      "Weil PCT-Anmeldungen immer ohne Priorität eingereicht werden.",
      "Weil die PCT-Frist immer 6 Monate beträgt.",
      "Weil Priorität nur für deutsche Patente gilt.",
    ],
    answer: 0,
    explanation:
      "Die PCT-Anmeldung ist häufig ein strategischer Schritt innerhalb der Prioritätsfrist, um den Weg in mehrere Länder oder Regionen offen zu halten.",
    sourceIds: ["pct8"],
    levelNumber: 2,
    errorCategory: "Frist falsch eingeordnet",
    skillTags: ["pct", "fristen-prioritaet"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "pct-international-search",
    pathId,
    topic: "PCT",
    title: "Internationale Recherche",
    prompt: "Welche Funktion hat die internationale Recherche im PCT-Verfahren typischerweise?",
    options: [
      "Sie liefert eine frühe Einschätzung zum relevanten Stand der Technik und kann strategische Entscheidungen beeinflussen.",
      "Sie bindet die späteren nationalen Ämter in der materiellen Prüfung vollständig.",
      "Sie ersetzt die Anspruchsarbeit, wenn der Bericht keine besonders relevanten Dokumente nennt.",
      "Sie entscheidet nicht über Verletzung, kann aber die Verletzungsprüfung später ersetzen.",
    ],
    answer: 0,
    explanation:
      "Die internationale Recherche gibt wichtige Hinweise zum Stand der Technik. Sie ersetzt aber nicht die spätere Sachprüfung in nationalen oder regionalen Phasen.",
    sourceIds: ["pct15"],
    levelNumber: 3,
    errorCategory: "Verfahrensschritt verwechselt",
    skillTags: ["pct"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "patg-patentability",
    pathId,
    topic: "PatG",
    title: "§ 1 PatG als Einstieg",
    prompt: "Welche Grundanforderungen nennt § 1 PatG für patentfähige Erfindungen?",
    options: [
      "Neuheit, erfinderische Tätigkeit und gewerbliche Anwendbarkeit.",
      "Schöpfungshöhe, Originalität und Bekanntheit.",
      "Markterfolg, technische Schönheit und Investitionshöhe.",
      "Nur Neuheit.",
    ],
    answer: 0,
    explanation:
      "§ 1 PatG nennt als Grundanforderungen Neuheit, erfinderische Tätigkeit und gewerbliche Anwendbarkeit.",
    sourceIds: ["patg1"],
    levelNumber: 2,
    errorCategory: "Regel nicht erinnert",
    skillTags: ["patentierbarkeit"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "patg-novelty",
    pathId,
    topic: "PatG",
    title: "§ 3 PatG und Neuheit",
    prompt: "Welche Aussage passt zu § 3 PatG?",
    options: [
      "Eine Erfindung gilt als neu, wenn sie nicht zum Stand der Technik gehört.",
      "Neuheit hängt allein vom wirtschaftlichen Erfolg ab.",
      "Nur deutsche Veröffentlichungen zählen.",
      "Mündliche Offenbarungen sind immer irrelevant.",
    ],
    answer: 0,
    explanation:
      "§ 3 PatG knüpft die Neuheit daran, ob die Erfindung zum Stand der Technik gehört.",
    sourceIds: ["patg3"],
    levelNumber: 2,
    errorCategory: "Regel nicht erinnert",
    skillTags: ["neuheit"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "patg-scope-claims",
    pathId,
    topic: "PatG",
    title: "§ 14 PatG und Schutzbereich",
    prompt: "Woran knüpft § 14 PatG für den Schutzbereich des Patents zentral an?",
    options: [
      "An den Inhalt der Patentansprüche, wobei Beschreibung und Zeichnungen zur Auslegung heranzuziehen sind.",
      "Nur an die Beschreibung.",
      "Nur an die Zeichnungen.",
      "An die Marketingunterlagen des Patentinhabers.",
    ],
    answer: 0,
    explanation:
      "Der Schutzbereich wird durch die Patentansprüche bestimmt; Beschreibung und Zeichnungen dienen der Auslegung.",
    sourceIds: ["patg14"],
    levelNumber: 3,
    errorCategory: "Anspruchslogik verwechselt",
    skillTags: ["claim-analysis"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "bgb-offer-acceptance",
    pathId,
    topic: "BGB",
    title: "Vertragsschluss als Grundstruktur",
    prompt: "Welche Grundstruktur steht typischerweise hinter einem Vertragsschluss im BGB?",
    options: [
      "Zwei übereinstimmende Willenserklärungen: Angebot und Annahme.",
      "Nur eine Rechnung.",
      "Nur eine E-Mail ohne Rechtsbindungswillen.",
      "Immer notarielle Beurkundung.",
    ],
    answer: 0,
    explanation:
      "Der Vertragsschluss wird klassisch über zwei korrespondierende Willenserklärungen beschrieben: Angebot und Annahme.",
    sourceIds: ["bgb145"],
    levelNumber: 1,
    errorCategory: "Grundstruktur verwechselt",
    skillTags: ["bgb-at"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "bgb-representation",
    pathId,
    topic: "BGB",
    title: "Stellvertretung",
    prompt: "Was ist ein Kerngedanke der Stellvertretung im BGB?",
    options: [
      "Eine Person kann unter bestimmten Voraussetzungen eine Willenserklärung mit Wirkung für und gegen eine andere Person abgeben.",
      "Vertretung ist im Zivilrecht immer ausgeschlossen.",
      "Vertretung ersetzt jede Vollmacht.",
      "Vertretung betrifft nur Gerichtsverfahren.",
    ],
    answer: 0,
    explanation:
      "Stellvertretung bedeutet, dass rechtsgeschäftliches Handeln einer Person unter bestimmten Voraussetzungen einem anderen zugerechnet wird.",
    sourceIds: ["bgb164"],
    levelNumber: 2,
    errorCategory: "Systemlogik verwechselt",
    skillTags: ["bgb-at"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "bgb-limitation",
    pathId,
    topic: "BGB",
    title: "Verjährung als Prüfungsfrage",
    prompt: "Was bewirkt Verjährung im Grundsatz?",
    options: [
      "Der Anspruch erlischt immer automatisch.",
      "Der Schuldner erhält grundsätzlich eine Einrede, mit der er die Leistung verweigern kann.",
      "Der Vertrag wird rückwirkend unwirksam.",
      "Verjährung betrifft nur Strafrecht.",
    ],
    answer: 1,
    explanation:
      "Verjährung führt grundsätzlich zu einer Einrede. Der Anspruch besteht nicht einfach automatisch nicht mehr, aber er ist unter Umständen nicht mehr durchsetzbar.",
    sourceIds: ["bgb194"],
    levelNumber: 2,
    errorCategory: "Rechtsfolge verwechselt",
    skillTags: ["bgb-at"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "epc-closest-prior-art-choice",
    pathId,
    topic: "Problem-Lösungs-Ansatz",
    title: "Nächstliegender Stand der Technik",
    prompt:
      "Zwei Dokumente liegen vor: D1 betrifft denselben Zweck wie die Erfindung, offenbart aber weniger Merkmale. D2 hat mehr gemeinsame Merkmale, betrifft aber ein anderes technisches Ziel. Welche Überlegung ist im Problem-Lösungs-Ansatz am tragfähigsten?",
    options: [
      "D2 ist automatisch nächstliegend, weil die meisten Merkmale übereinstimmen.",
      "D1 kann trotz weniger gemeinsamer Merkmale näher liegen, wenn es denselben Zweck oder dieselbe Wirkung adressiert.",
      "Der nächstliegende Stand der Technik wird immer durch den Anmelder frei gewählt.",
      "Beide Dokumente werden für Neuheit zu einem einzigen Dokument kombiniert.",
    ],
    answer: 1,
    explanation:
      "Der nächstliegende Stand der Technik ist nicht rein mechanisch die Merkmalsliste mit den meisten Treffern. Zweck, Wirkung und realistischer Ausgangspunkt des Fachmanns sind zentral.",
    sourceIds: ["psaClosest"],
    levelNumber: 4,
    errorCategory: "Ausgangspunkt falsch gewichtet",
    skillTags: ["erfinderische-taetigkeit"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "epc-123-2-intermediate-generalisation",
    pathId,
    topic: "Änderungen",
    title: "Zwischenverallgemeinerung",
    prompt:
      "Die ursprüngliche Anmeldung offenbart eine Kombination aus A+B+C. Im Prüfungsverfahren soll nur A+B beansprucht werden, weil C für den Schutzumfang stört. Was ist der zentrale Prüfpunkt?",
    options: [
      "Ob A+B ohne C für den Fachmann direkt und eindeutig als eigene technische Lehre offenbart war.",
      "Ob C wirtschaftlich weniger wichtig ist als A+B.",
      "Ob die Änderung den Anspruch kürzer macht.",
      "Ob der Prüfer C in der Recherche besonders hervorgehoben hat.",
    ],
    answer: 0,
    explanation:
      "Eine Zwischenverallgemeinerung kann unzulässig sein, wenn aus einer konkret offenbarten Kombination einzelne Merkmale herausgelöst werden, ohne dass diese Herauslösung ursprünglich gestützt ist.",
    sourceIds: ["addedMatter"],
    levelNumber: 4,
    errorCategory: "Offenbarungsbasis zu grob geprüft",
    skillTags: ["added-matter"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "patg-section-9-infringement-acts",
    pathId,
    topic: "PatG",
    title: "§ 9 PatG praktisch lesen",
    prompt:
      "Ein Wettbewerber bietet in Deutschland ein patentgemäßes Erzeugnis online an, liefert aber noch nicht aus. Welche Einordnung liegt nach § 9 PatG am nächsten?",
    options: [
      "Auch das Anbieten kann eine relevante Benutzungshandlung sein.",
      "Erst die tatsächliche Lieferung kann relevant sein.",
      "Online-Angebote sind patentrechtlich nie relevant.",
      "§ 9 PatG betrifft nur Verfahren, nicht Erzeugnisse.",
    ],
    answer: 0,
    explanation:
      "§ 9 PatG nennt bei Erzeugnissen unter anderem Herstellen, Anbieten, Inverkehrbringen, Gebrauchen sowie Einführen oder Besitzen zu diesen Zwecken.",
    sourceIds: ["patg9"],
    levelNumber: 3,
    errorCategory: "Benutzungshandlung zu eng verstanden",
    skillTags: ["verletzung"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "bgb-invitatio-offer",
    pathId,
    topic: "BGB",
    title: "Angebot oder nur Verhandlungsanstoß?",
    prompt:
      "Ein Unternehmen schreibt: 'Wir könnten die Technologie grundsätzlich für 50.000 EUR lizenzieren, Details müssten wir noch verhandeln.' Was ist der zentrale Prüfpunkt?",
    options: [
      "Ob bereits ein hinreichend bestimmtes Angebot mit Rechtsbindungswillen vorliegt oder nur eine Einladung zu weiteren Verhandlungen.",
      "Ob jede Preisangabe automatisch ein verbindliches Angebot ist.",
      "Ob technische Schutzrechte Vertragsschluss ausschließen.",
      "Ob ein Vertrag nur bei notarieller Beurkundung entstehen kann.",
    ],
    answer: 0,
    explanation:
      "Für Vertragsschluss kommt es nicht nur auf einen Preis an. Entscheidend sind Bestimmtheit und Rechtsbindungswille der Erklärung.",
    sourceIds: ["bgb145"],
    levelNumber: 3,
    errorCategory: "Willenserklärung zu schnell angenommen",
    skillTags: ["bgb-at"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "bgb-representation-undisclosed",
    pathId,
    topic: "BGB",
    title: "Offenkundigkeit bei Stellvertretung",
    prompt:
      "Eine Person verhandelt im eigenen Namen, meint innerlich aber, für ein Unternehmen zu handeln. Welche Frage ist für Stellvertretung besonders wichtig?",
    options: [
      "Ob nach außen erkennbar im Namen des Vertretenen gehandelt wurde.",
      "Ob die Person fachlich kompetent war.",
      "Ob der Vertrag wirtschaftlich sinnvoll ist.",
      "Ob die andere Seite später von der inneren Absicht erfährt.",
    ],
    answer: 0,
    explanation:
      "Stellvertretung setzt grundsätzlich voraus, dass die Erklärung im Namen des Vertretenen abgegeben wird. Die bloß innere Absicht genügt nicht.",
    sourceIds: ["bgb164"],
    levelNumber: 3,
    errorCategory: "Außenwirkung übersehen",
    skillTags: ["bgb-at"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "epc-novelty-selection-from-list",
    pathId,
    topic: "Neuheit",
    title: "Auswahlerfindung sauber prüfen",
    prompt:
      "Ein älteres Dokument offenbart eine allgemeine Stoffklasse mit vielen Alternativen. Der Anspruch betrifft eine konkrete Untergruppe. Welche Frage ist für Neuheit besonders wichtig?",
    options: [
      "Ob die konkrete Untergruppe für den Fachmann unmittelbar und eindeutig aus der älteren Offenbarung hervorgeht.",
      "Ob die Untergruppe wirtschaftlich interessanter ist als die allgemeine Klasse.",
      "Ob die ältere Offenbarung aus demselben Land stammt.",
      "Ob der Anspruch eine kürzere Formulierung verwendet.",
    ],
    answer: 0,
    explanation:
      "Bei Auswahlkonstellationen reicht eine breite generische Offenbarung nicht automatisch für jede spätere konkrete Auswahl. Entscheidend bleibt die unmittelbare und eindeutige Offenbarung.",
    sourceIds: ["art54"],
    levelNumber: 4,
    errorCategory: "Offenbarungsmaßstab unscharf",
    skillTags: ["neuheit"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "epc-inventive-step-technical-effect",
    pathId,
    topic: "Erfinderische Tätigkeit",
    title: "Technischer Effekt als Drehpunkt",
    prompt:
      "Ein Anspruch unterscheidet sich von D1 nur durch Merkmal X. X erzeugt aber keinen plausiblen technischen Effekt gegenüber D1. Was ist im Problem-Lösungs-Ansatz naheliegend?",
    options: [
      "Die objektive technische Aufgabe wird weniger ambitioniert formuliert, etwa als Bereitstellung einer Alternative.",
      "Der Anspruch ist automatisch neuheitsschädlich vorweggenommen.",
      "X darf bei erfinderischer Tätigkeit nie berücksichtigt werden.",
      "Die Anmeldung wird allein wegen fehlender gewerblicher Anwendbarkeit zurückgewiesen.",
    ],
    answer: 0,
    explanation:
      "Ohne tragfähigen technischen Effekt trägt das Unterscheidungsmerkmal regelmäßig keine anspruchsvollere technische Aufgabe. Dann verschiebt sich die Prüfung in Richtung bloßer Alternative.",
    sourceIds: ["psaProblem"],
    levelNumber: 3,
    errorCategory: "Technische Wirkung falsch eingeordnet",
    skillTags: ["erfinderische-taetigkeit"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "epc-priority-partial",
    pathId,
    topic: "Priorität",
    title: "Priorität kann teilweise tragen",
    prompt:
      "Ein Anspruch umfasst Variante A und Variante B. Nur A war in der Prioritätsanmeldung offenbart. Welche Sicht ist am saubersten?",
    options: [
      "Die Priorität kann für A tragen, während B gesondert gegen späteren Stand der Technik zu prüfen ist.",
      "Der gesamte Anspruch verliert automatisch jede Priorität.",
      "B erhält Priorität, wenn A wirtschaftlich denselben Zweck hat.",
      "Priorität hängt nur vom Namen des Anmelders ab.",
    ],
    answer: 0,
    explanation:
      "Prioritätsfragen können gegenstandsbezogen zu prüfen sein. Entscheidend ist, welche technische Lehre in der Prioritätsanmeldung unmittelbar und eindeutig offenbart war.",
    sourceIds: ["art87"],
    levelNumber: 4,
    errorCategory: "Gegenstandsbezogene Prüfung übersehen",
    skillTags: ["fristen-prioritaet"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "patg-indirect-infringement",
    pathId,
    topic: "PatG",
    title: "Mittelbare Patentverletzung",
    prompt:
      "Ein Zulieferer bietet ein wesentliches Bauteil an, das objektiv für die Benutzung einer patentierten Erfindung bestimmt ist. Welche Frage liegt nahe?",
    options: [
      "Ob eine mittelbare Patentverletzung in Betracht kommt.",
      "Ob Patentverletzung nur bei vollständig montierten Endprodukten möglich ist.",
      "Ob das Bauteil markenrechtlich geschützt ist.",
      "Ob die Patentanmeldung noch unveröffentlicht ist.",
    ],
    answer: 0,
    explanation:
      "Neben unmittelbaren Benutzungshandlungen kann auch das Anbieten oder Liefern von Mitteln für die Benutzung der Erfindung relevant werden. Die konkrete Prüfung hängt stark vom Sachverhalt ab.",
    sourceIds: ["patg10"],
    levelNumber: 4,
    errorCategory: "Verletzungsprüfung zu eng angesetzt",
    skillTags: ["verletzung"],
    reviewStatus: "beta-ready",
  }),

  makeSingle(sources, {
    id: "bgb-condition-precedent",
    pathId,
    topic: "BGB",
    title: "Bedingung in Lizenzverhandlungen",
    prompt:
      "Eine Vereinbarung soll erst gelten, wenn ein bestimmtes Patent erteilt wird. Welche zivilrechtliche Denkfigur ist naheliegend?",
    options: [
      "Eine aufschiebende Bedingung.",
      "Eine Anfechtung wegen Irrtums.",
      "Eine Verjährungseinrede.",
      "Eine Geschäftsführung ohne Auftrag.",
    ],
    answer: 0,
    explanation:
      "Wenn die Rechtswirkungen eines Geschäfts vom Eintritt eines zukünftigen ungewissen Ereignisses abhängen sollen, liegt die Prüfung einer Bedingung nahe.",
    sourceIds: ["bgb158"],
    levelNumber: 3,
    errorCategory: "Zivilrechtliche Struktur nicht erkannt",
    skillTags: ["bgb-at"],
    reviewStatus: "beta-ready",
  }),

  // ─── MULTI (8) ──────────────────────────────────────────────────────────────

  makeMulti(sources, {
    id: "added-matter-ai-optimized",
    pathId,
    topic: "Änderungen",
    title: "Klingt enger, kann aber neu sein",
    prompt:
      "Die ursprüngliche Anmeldung beschreibt einen 'Regler'. Später soll daraus ein 'KI-optimierter Regler' werden, ohne dass KI ursprünglich offenbart war. Welche Risiken sind in dieser Konstellation ernsthaft zu prüfen?",
    options: [
      "Art. 123(2) EPÜ, weil neue technische Information eingeführt werden könnte.",
      "Art. 87 EPÜ, wenn die Prioritätsanmeldung zwar einen Regler, aber keine KI-Optimierung offenbart.",
      "Art. 56 EPÜ, wenn der KI-Bezug im Stand der Technik bereits nahegelegt war.",
      "Kein Problem, weil die Änderung enger klingt.",
    ],
    answers: [0, 1, 2] as [number, number, number],
    explanation:
      "Art. 123(2) ist der unmittelbare Änderungspunkt. Zusätzlich können Priorität und erfinderische Tätigkeit relevant werden, wenn dieselbe technische Information dort eine Rolle spielt. 'Enger klingend' ersetzt keine Offenbarungsbasis.",
    sourceIds: ["addedMatter"],
    levelNumber: 4,
    errorCategory: "Voraussetzung verwechselt",
    skillTags: ["added-matter"],
    reviewStatus: "beta-ready",
  }),

  makeMulti(sources, {
    id: "pct-iprp-strategic-weight",
    pathId,
    topic: "PCT",
    title: "Vorläufige Einschätzung im PCT",
    prompt:
      "Der internationale vorläufige Bericht ist kritisch zu Neuheit und erfinderischer Tätigkeit. Welche Reaktionen sind strategisch sinnvoll?",
    options: [
      "Den Bericht als frühes Risikosignal nutzen und Anspruchssatz, Länderstrategie und Kostenentscheidung prüfen.",
      "Prüfen, ob eine geänderte Anspruchsfassung oder Argumentationslinie vor Eintritt in kostenintensive Phasen sinnvoll ist.",
      "Den Bericht als verbindliche Entscheidung aller späteren nationalen Ämter behandeln.",
      "Die Einschätzung nur dann ernst nehmen, wenn sie von jedem späteren Bestimmungsamt identisch übernommen werden muss.",
    ],
    answers: [0, 1] as [number, number],
    explanation:
      "PCT-Berichte binden spätere Ämter nicht abschließend. Gerade deshalb sind sie als Frühindikator wertvoll: Sie helfen, Anspruchsarbeit, Argumentation, Länderstrategie und Budget vor hohen Folgekosten zu prüfen.",
    sourceIds: ["pct33"],
    levelNumber: 3,
    errorCategory: "Strategische Bedeutung falsch eingeordnet",
    skillTags: ["pct"],
    reviewStatus: "beta-ready",
  }),

  makeMulti(sources, {
    id: "pct-national-phase-business",
    pathId,
    topic: "PCT",
    title: "Nationale Phase als Geschäftsentscheidung",
    prompt:
      "Warum ist der Eintritt in nationale oder regionale Phasen selten nur eine formale Fristfrage?",
    options: [
      "Weil dann entschieden wird, in welchen Märkten Kosten, Durchsetzungschancen und wirtschaftliche Relevanz Schutz rechtfertigen.",
      "Weil Rechercheergebnisse, Produktfokus und Wettbewerbsumfeld vor der Länderentscheidung neu gewichtet werden können.",
      "Weil ab diesem Moment die internationale Anmeldung automatisch in ein einheitliches Weltpatent übergeht.",
      "Weil nationale Phasen nur dann relevant sind, wenn bereits ein Verletzungsfall sicher feststeht.",
    ],
    answers: [0, 1] as [number, number],
    explanation:
      "Der PCT hält Optionen offen. Die eigentliche Portfoliologik entsteht spätestens bei der Auswahl der Länder oder Regionen, in denen Kosten, Marktinteresse, Stand der Technik und Durchsetzungsperspektive zusammenpassen müssen.",
    sourceIds: ["pct11"],
    levelNumber: 3,
    errorCategory: "Business-Kontext übersehen",
    skillTags: ["pct"],
    reviewStatus: "beta-ready",
  }),

  makeMulti(sources, {
    id: "pct-isa-written-opinion",
    pathId,
    topic: "PCT",
    title: "Written Opinion richtig nutzen",
    prompt:
      "Die Written Opinion sieht einen Einwand wegen fehlender erfinderischer Tätigkeit. Was ist die beste Arbeitsreaktion?",
    options: [
      "Anspruchsmerkmale, technische Wirkung und mögliche Hilfsargumente prüfen, bevor hohe nationale Kosten ausgelöst werden.",
      "Bewerten, ob die Länderstrategie noch zum wirtschaftlichen Wert der Erfindung passt.",
      "Den Einwand als verbindliche endgültige Ablehnung aller späteren Ämter behandeln.",
      "Nur formale Daten prüfen, weil materielle Einwände im PCT nie strategische Bedeutung haben.",
    ],
    answers: [0, 1] as [number, number],
    explanation:
      "Die Written Opinion ist kein endgültiges Urteil aller späteren Ämter. Sie kann aber Anspruchsstrategie, Argumentation, Länderwahl und Budgetentscheidung sehr konkret beeinflussen.",
    sourceIds: ["pct33"],
    levelNumber: 4,
    errorCategory: "Verfahrenssignal falsch genutzt",
    skillTags: ["pct"],
    reviewStatus: "beta-ready",
  }),

  makeMulti(sources, {
    id: "patg-section-14-claim-interpretation",
    pathId,
    topic: "PatG",
    title: "Schutzbereich ist Anspruchsarbeit",
    prompt:
      "Ein Anspruch verwendet einen breiten technischen Begriff. Die Beschreibung zeigt mehrere Ausführungsformen, aber keine ausdrückliche Einschränkung auf eine davon. Welche Aussagen sind vorsichtig und methodisch sauber?",
    options: [
      "Der Anspruch wird durch seinen Wortlaut bestimmt; Beschreibung und Zeichnungen dienen der Auslegung, ersetzen den Anspruch aber nicht.",
      "Ausführungsbeispiele können für die Auslegung wichtig sein, begrenzen den Anspruch aber nicht automatisch auf die engste Variante.",
      "Die Beschreibung ist für die Auslegung irrelevant, sobald der Anspruch grammatikalisch eindeutig formuliert ist.",
      "Der Schutzbereich richtet sich vorrangig nach dem späteren Produkt des Patentinhabers.",
    ],
    answers: [0, 1] as [number, number],
    explanation:
      "§ 14 PatG macht den Anspruch zum Ausgangspunkt. Beschreibung und Zeichnungen sind wichtig für die Auslegung, begrenzen den Schutzbereich aber nicht automatisch auf ein einzelnes Ausführungsbeispiel.",
    sourceIds: ["patg14"],
    levelNumber: 4,
    errorCategory: "Schutzbereich zu schematisch gelesen",
    skillTags: ["claim-analysis"],
    reviewStatus: "beta-ready",
  }),

  makeMulti(sources, {
    id: "patg-all-none-scope-use",
    pathId,
    topic: "PatG",
    title: "Schutzbereich und Benutzung",
    prompt:
      "Welche Aussagen passen methodisch zu Schutzbereich und Benutzungshandlungen nach PatG?",
    options: [
      "Für den Schutzbereich ist der Inhalt der Patentansprüche der Ausgangspunkt.",
      "Beschreibung und Zeichnungen können für die Auslegung der Patentansprüche herangezogen werden.",
      "Das Anbieten eines patentgemäßen Erzeugnisses kann eine relevante Benutzungshandlung sein.",
      "Der Schutzbereich richtet sich ausschließlich nach dem späteren Geschäftsmodell des Patentinhabers.",
    ],
    answers: [0, 1, 2] as [number, number, number],
    explanation:
      "Anspruch, Beschreibung und Zeichnungen stehen in einer Auslegungslogik. Zusätzlich kann bereits das Anbieten eine relevante Benutzungshandlung sein. Das Geschäftsmodell ersetzt diese Prüfung nicht.",
    sourceIds: ["patg14"],
    levelNumber: 4,
    errorCategory: "Schutzbereich und Benutzung vermischt",
    skillTags: ["claim-analysis", "verletzung"],
    reviewStatus: "beta-ready",
  }),

  makeMulti(sources, {
    id: "bgb-contract-formation-multi",
    pathId,
    topic: "BGB",
    title: "Vertragsschluss in Lizenzverhandlungen",
    prompt:
      "In einer Lizenzverhandlung werden Preis, Vertragsgegenstand und Laufzeit diskutiert. Welche Aussagen sind für die Prüfung eines Vertragsschlusses methodisch tragfähig?",
    options: [
      "Ein Vertragsschluss setzt regelmäßig zwei inhaltlich korrespondierende Willenserklärungen voraus.",
      "Eine Erklärung kann trotz Preisangabe noch unverbindlich sein, wenn wesentliche Punkte ausdrücklich offenbleiben.",
      "Ein Rechtsbindungswille ist für die Einordnung einer Erklärung als Angebot relevant.",
      "Jede technische Beschreibung eines Lizenzgegenstands ist automatisch ein verbindliches Angebot.",
    ],
    answers: [0, 1, 2] as [number, number, number],
    explanation:
      "Angebot und Annahme brauchen Bestimmtheit und Rechtsbindungswillen. Eine Preisangabe kann ein starkes Indiz sein, ersetzt aber nicht die Prüfung, ob die Erklärung bereits bindend gemeint war.",
    sourceIds: ["bgb145"],
    levelNumber: 3,
    errorCategory: "Vertragsschluss zu schematisch geprüft",
    skillTags: ["bgb-at"],
    reviewStatus: "beta-ready",
  }),

  makeMulti(sources, {
    id: "bgb-representation-multi",
    pathId,
    topic: "BGB",
    title: "Stellvertretung sauber prüfen",
    prompt:
      "Eine Person verhandelt über eine technische Kooperation und unterschreibt 'für das Projektteam', ohne das Unternehmen klar zu nennen. Welche Punkte sind für Stellvertretung besonders prüfungsrelevant?",
    options: [
      "Ob die Erklärung erkennbar im Namen eines Vertretenen abgegeben wurde.",
      "Ob Vertretungsmacht bestand oder später genehmigt werden könnte.",
      "Ob der Vertrag wirtschaftlich vorteilhaft war, denn dann ist Vertretungsmacht entbehrlich.",
      "Ob sich aus den Umständen ergibt, wer berechtigt und verpflichtet werden sollte.",
    ],
    answers: [0, 1, 3] as [number, number, number],
    explanation:
      "Bei Stellvertretung sind Offenkundigkeit, Vertretungsmacht und die Auslegung der Erklärung zentral. Wirtschaftlicher Nutzen ersetzt keine Vertretungsmacht.",
    sourceIds: ["bgb164"],
    levelNumber: 4,
    errorCategory: "Vertretungslogik verkürzt",
    skillTags: ["bgb-at"],
    reviewStatus: "beta-ready",
  }),

  // ─── ALLNONE (3) ────────────────────────────────────────────────────────────

  {
    id: "epc-all-none-novelty-disclosure",
    pathId,
    mode: "epc",
    kind: "allnone",
    topic: "Neuheit",
    title: "Neuheit: welche Aussagen tragen?",
    prompt:
      "Welche Aussagen zur Neuheitsprüfung sind zutreffend? Es können alle, keine oder mehrere Antworten richtig sein.",
    options: [
      "Ein einzelnes Dokument muss die beanspruchte Merkmalskombination unmittelbar und eindeutig offenbaren.",
      "Eine mündliche Offenbarung kann grundsätzlich Stand der Technik sein.",
      "Für die Neuheit dürfen mehrere Dokumente nicht kombiniert werden (Einzelvergleich) — anders als bei der erfinderischen Tätigkeit, bei der eine Zusammenschau mehrerer Dokumente zulässig ist.",
      "Öffentliche Zugänglichkeit ist wichtiger als die Frage, ob der Anmelder die Offenbarung tatsächlich kannte.",
    ],
    answers: [0, 1, 2, 3],
    explanation:
      "Alle Aussagen treffen zu: Neuheit verlangt den Einzelvergleich (eine einzige Offenbarung muss alle Merkmale unmittelbar und eindeutig zeigen, Art. 54 EPÜ); eine mündliche Offenbarung zählt zum Stand der Technik (Art. 54(2) EPÜ); maßgeblich ist die objektive öffentliche Zugänglichkeit, nicht die subjektive Kenntnis; und anders als bei der Neuheit ist für die erfinderische Tätigkeit eine Zusammenschau mehrerer Dokumente zulässig (Art. 56 EPÜ).",
    sourceIds: ["art54"],
    sources: [sources.art54],
    level: "Pruefungsstabil",
    levelNumber: 4,
    errorCategory: "Neuheitsmaßstab unscharf",
    skillTags: ["neuheit"],
    reviewStatus: "beta-ready",
  } as LearningExercise,

  {
    id: "pct-none-world-patent",
    pathId,
    mode: "epc",
    kind: "allnone",
    topic: "PCT",
    title: "PCT: welche Aussage stimmt nicht?",
    prompt:
      "Welche der folgenden Aussagen ist zutreffend? Es können alle, keine oder mehrere Antworten richtig sein.",
    options: [
      "Die internationale Anmeldung führt automatisch zur Erteilung eines weltweit einheitlichen Patents.",
      "Ein kritischer internationaler Bericht beendet das Verfahren automatisch in allen Staaten.",
      "Mit Eintritt in die nationale Phase entfällt jede Möglichkeit, Anspruchsfassungen noch strategisch zu bearbeiten.",
      "Die internationale Recherche entscheidet verbindlich über spätere Patentverletzung.",
    ],
    answers: [],
    explanation:
      "Keine der Aussagen ist zutreffend. Der PCT eröffnet und strukturiert ein internationales Anmeldeverfahren, ersetzt aber weder nationale Prüfungen noch strategische Anspruchsarbeit.",
    sourceIds: ["pct3"],
    sources: [sources.pct3],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "PCT-Systemlogik überschätzt",
    skillTags: ["pct"],
    reviewStatus: "beta-ready",
  } as LearningExercise,

  {
    id: "bgb-none-form-license",
    pathId,
    mode: "epc",
    kind: "allnone",
    topic: "BGB",
    title: "Formfragen nicht vorschnell annehmen",
    prompt:
      "Welche der folgenden Aussagen sind für einen gewöhnlichen schuldrechtlichen Vertragsschluss allgemein zutreffend? Es können alle, keine oder mehrere Antworten richtig sein.",
    options: [
      "Ein schuldrechtlicher Vertrag bedarf zu seiner Wirksamkeit grundsätzlich der Schriftform.",
      "Eine Preisauszeichnung von Waren im Schaufenster ist bereits ein bindendes Vertragsangebot.",
      "Eine Vertragsannahme ist nur wirksam, wenn sie ausdrücklich erklärt wird.",
      "Eine spätere schriftliche Zusammenfassung entscheidet allein darüber, ob bereits ein Vertrag zustande gekommen ist.",
    ],
    answers: [],
    explanation:
      "Keine Aussage trifft allgemein zu: Schuldrechtliche Verträge sind grundsätzlich formfrei (Schriftform nur bei gesetzlicher Anordnung, § 125 BGB). Eine Schaufensterauslage ist bloße invitatio ad offerendum, kein bindendes Angebot (§ 145 BGB). Die Annahme kann auch konkludent oder nach § 151 BGB ohne Zugang erfolgen. Und für das Zustandekommen sind Angebot und Annahme maßgeblich, nicht eine spätere schriftliche Zusammenfassung.",
    sourceIds: ["bgb145"],
    sources: [sources.bgb145],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Form und Bindung vorschnell angenommen",
    skillTags: ["bgb-at"],
    reviewStatus: "beta-ready",
  } as LearningExercise,
];
